import { useMemo, type ComponentType } from "react";
import type { PortalSchema } from "../../shared/schema";

/**
 * Known legacy route slugs that should be handled by legacy page components
 * when no matching portal schema page exists.
 */
export const LEGACY_ROUTE_SLUGS = ["documents", "events", "content", "book-an-appointment", "communications"] as const;

export type LegacyRouteSlug = (typeof LEGACY_ROUTE_SLUGS)[number];

/**
 * Result of legacy route resolution.
 */
export interface LegacyRouteResolution {
  /** Whether the slug matches a page in the portal schema */
  isSchemaPage: boolean;
  /** Whether the slug is a known legacy route */
  isLegacyRoute: boolean;
  /** The legacy route slug if it's a known legacy route */
  legacySlug: LegacyRouteSlug | null;
  /** Whether the route is valid (either schema page or legacy route) */
  isValidRoute: boolean;
}

/**
 * Hook that resolves URL slugs to determine whether they should be handled
 * by portal schema pages or legacy route fallbacks.
 *
 * Priority:
 * 1. If slug matches a page in the portal schema -> render schema page
 * 2. If slug is a known legacy route -> render legacy component
 * 3. Otherwise -> invalid route (redirect to home)
 *
 * This ensures that portal configuration always takes precedence over
 * legacy route fallbacks, allowing organizations to customize pages
 * with matching slugs.
 *
 * @param schema - The portal schema containing pages, or null if not yet loaded
 * @param slug - The URL slug to resolve (from URL params)
 * @returns Resolution result indicating how to handle the route
 */
export const useLegacyRouteResolver = (
  schema: PortalSchema | null,
  slug: string | undefined
): LegacyRouteResolution => {
  return useMemo(() => {
    // No slug means root "/" - always valid, show first page
    if (!slug) {
      return {
        isSchemaPage: true,
        isLegacyRoute: false,
        legacySlug: null,
        isValidRoute: true,
      };
    }

    // Check if slug matches a page in the portal schema
    const schemaPage = schema?.pages.find((p) => p.slug === slug);
    if (schemaPage) {
      return {
        isSchemaPage: true,
        isLegacyRoute: false,
        legacySlug: null,
        isValidRoute: true,
      };
    }

    // Check if slug is a known legacy route
    const isLegacy = LEGACY_ROUTE_SLUGS.includes(slug as LegacyRouteSlug);
    if (isLegacy) {
      return {
        isSchemaPage: false,
        isLegacyRoute: true,
        legacySlug: slug as LegacyRouteSlug,
        isValidRoute: true,
      };
    }

    // Unknown slug - invalid route
    return {
      isSchemaPage: false,
      isLegacyRoute: false,
      legacySlug: null,
      isValidRoute: false,
    };
  }, [schema, slug]);
};

/**
 * Type for lazy-loaded legacy page components.
 */
export type LegacyPageComponent = ComponentType<Record<string, never>>;
